import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Memory,
  Speed,
  Storage,
  Person,
  AttachMoney,
  Assessment,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { apiCall } from '../utils/api';
import RealTimeMetrics from '../components/RealTimeMetrics';
import { useAuth } from '../contexts/AuthContext';

interface MonitoringDashboard {
  active_users: number;
  total_api_calls_today: number;
  total_tokens_today: number;
  total_cost_today: number;
  files_processed_today: number;
  avg_response_time_ms: number;
  error_rate: number;
  memory_usage_percent: number;
  cpu_usage_percent: number;
  disk_usage_percent: number;
  recent_llm_calls: Array<{
    timestamp: string;
    user_id: string;
    model_name: string;
    total_tokens: number;
    cost_usd: number;
    operation_type: string;
  }>;
  recent_user_activity: Array<{
    timestamp: string;
    user_id: string;
    activity_type: string;
    endpoint: string;
    success: boolean;
  }>;
  recent_file_processing: Array<{
    timestamp: string;
    user_id: string;
    filename: string;
    file_size_bytes: number;
    processing_stage: string;
    success: boolean;
  }>;
}

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
}> = ({ title, value, icon, color = 'primary', subtitle }) => (
  <Card sx={{ minHeight: 120 }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box color={`${color}.main`}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const MonitoringPage: React.FC = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<MonitoringDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      const dashboardRes = await apiCall('/api/monitoring/dashboard');
      
      if (!dashboardRes.ok) {
        if (dashboardRes.status === 403) {
          throw new Error('Access denied: Admin privileges required');
        }
        throw new Error(`HTTP ${dashboardRes.status}: ${dashboardRes.statusText}`);
      }
      
      const dashboardData = await dashboardRes.json();
      setDashboard(dashboardData);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch monitoring data:', err);
      setError(err.message || 'Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchMonitoringData();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchMonitoringData, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) return '$0.000000';
    return `$${amount.toFixed(6)}`;
  };
  
  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    return num.toLocaleString();
  };

  // Check if user has admin role
  if (user?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access Denied: Admin privileges required to view monitoring dashboard.
        </Alert>
      </Box>
    );
  }

  if (loading && !dashboard) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!dashboard) {
    return <Alert severity="info">No monitoring data available</Alert>;
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Application Monitoring Dashboard
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="Recent Activity" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          {/* Real-time Metrics Section */}
          <Box mb={4}>
            <RealTimeMetrics
              metrics={[
                {
                  title: 'Memory Usage',
                  value: dashboard.memory_usage_percent || 0,
                  unit: '%',
                  threshold: { warning: 70, error: 90 },
                  color: 'primary'
                },
                {
                  title: 'CPU Usage',
                  value: dashboard.cpu_usage_percent || 0,
                  unit: '%',
                  threshold: { warning: 80, error: 95 },
                  color: 'secondary'
                },
                {
                  title: 'Response Time',
                  value: dashboard.avg_response_time_ms || 0,
                  unit: 'ms',
                  threshold: { warning: 1000, error: 3000 },
                  color: 'info'
                },
                {
                  title: 'Active Users',
                  value: dashboard.active_users || 0,
                  unit: 'users',
                  color: 'success'
                }
              ]}
              onRefresh={fetchMonitoringData}
            />
          </Box>
          
          {/* Key Metrics Grid */}
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={3} mb={3}>
            <MetricCard
              title="Active Users Today"
              value={formatNumber(dashboard.active_users)}
              icon={<Person fontSize="large" />}
              color="primary"
            />
            
            <MetricCard
              title="API Calls Today"
              value={formatNumber(dashboard.total_api_calls_today)}
              icon={<Assessment fontSize="large" />}
              color="secondary"
            />
            
            <MetricCard
              title="Tokens Consumed"
              value={formatNumber(dashboard.total_tokens_today)}
              icon={<TrendingUp fontSize="large" />}
              color="success"
              subtitle="Today"
            />
            
            <MetricCard
              title="Cost Today"
              value={formatCurrency(dashboard.total_cost_today)}
              icon={<AttachMoney fontSize="large" />}
              color="warning"
            />
          </Box>

          {/* Performance Metrics */}
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={3}>
            <MetricCard
              title="Files Processed"
              value={formatNumber(dashboard.files_processed_today)}
              icon={<Assessment fontSize="large" />}
              color="info"
              subtitle="Today"
            />
            
            <MetricCard
              title="Avg Response Time"
              value={`${(dashboard.avg_response_time_ms || 0).toFixed(0)}ms`}
              icon={<Speed fontSize="large" />}
              color={(dashboard.avg_response_time_ms || 0) > 1000 ? 'warning' : 'success'}
            />
            
            <MetricCard
              title="Error Rate"
              value={`${(dashboard.error_rate || 0).toFixed(2)}%`}
              icon={<ErrorIcon fontSize="large" />}
              color={(dashboard.error_rate || 0) > 5 ? 'error' : 'success'}
            />
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent LLM Calls</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Tokens</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dashboard.recent_llm_calls || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No LLM calls recorded yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (dashboard.recent_llm_calls || []).slice(0, 5).map((call, index) => (
                        <TableRow key={index}>
                          <TableCell>{new Date(call.timestamp).toLocaleTimeString()}</TableCell>
                          <TableCell>{call.user_id || 'Unknown'}</TableCell>
                          <TableCell>{call.model_name || 'Unknown'}</TableCell>
                          <TableCell>{call.total_tokens || 0}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent User Activity</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Activity</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dashboard.recent_user_activity || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No user activity recorded yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (dashboard.recent_user_activity || []).slice(0, 5).map((activity, index) => (
                        <TableRow key={index}>
                          <TableCell>{new Date(activity.timestamp).toLocaleTimeString()}</TableCell>
                          <TableCell>{activity.user_id || 'Unknown'}</TableCell>
                          <TableCell>
                            <Chip label={activity.activity_type || 'Unknown'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={activity.success ? 'Success' : 'Failed'} 
                              color={activity.success ? 'success' : 'error'}
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default MonitoringPage;